import { QUERY_KEY } from "@/constant/queryKey";
import {
  changePassword,
  ChangePasswordData,
  login,
  register,
  type LoginCredentials,
  type RegisterData,
} from "@/services/auth/auth-service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface UseLoginOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

/**
 * Hook for handling login functionality
 */
export function useLogin(options: UseLoginOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => login(credentials),
    onSuccess: (data) => {
      // Invalidate any queries that might depend on auth state
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.USER] });

      toast.success("Login successful!", {
        description: data.message || "Redirecting to your dashboard...",
      });

      if (options.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error: any) => {
      toast.error("Login failed", {
        description:
          error?.response?.data?.message ||
          "Please check your credentials and try again.",
      });
    },
  });
}

/**
 * Hook for handling registration functionality
 */
export function useRegister(options: UseLoginOptions = {}) {
  return useMutation({
    mutationFn: (data: RegisterData) => register(data),
    onSuccess: (data) => {
      toast("Registration successful!", {
        description:
          data.message || "You can now sign in with your credentials.",
      });

      // Redirect to login page after successful registration
      if (options.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error: any) => {
      toast.error("Registration failed", {
        description:
          error?.response?.data?.message ||
          "Please check your information and try again.",
      });
    },
  });
}

/**
 * Hook for handling change password functionality
 */
export function useChangePassword(options: UseLoginOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePasswordData) => changePassword(data),
    onSuccess: (_data) => {
      // Invalidate any queries that might depend on auth state
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.USER] });

      toast.success("Change password successful!", {
        description: "You can now sign in back with your credentials...",
      });

      if (options.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error: any) => {
      toast.error("Change password failed", {
        description:
          error?.response?.data?.message ||
          "Please check your credentials and try again.",
      });
    },
  });
}
